/**
 * TestCTAButtons — Clon pixel-perfect de Index.tsx para la rama BaseTemplate.
 *
 * Copiado 1:1 de Index.tsx: mismos hooks, misma lógica SEO, mismo contexto,
 * mismo ArgentinaProvinceAdmin. La única diferencia es `heroCta` en BaseTemplate.
 *
 * COHERENCIA CON TEMPLATES:
 * ─────────────────────────
 * ✅ BaseTemplate countries (AR, UY, ES, AU…) → BirthdayButton
 *    → Estilo glass-morphism (bg-black/[0.08], fondo blanco) coherente con BaseTemplate.
 *    → Listo para pasar a producción en Index.tsx.
 *
 * ⏳ BioTemplatePremium countries (MX, BR, CO, Europa…) → WishlistButton
 *    → Se muestra en BaseTemplate (fondo blanco) SOLO para testear la lógica de detección.
 *    → Cuando vaya a BioTemplatePremium (fondo negro) necesitará estilo dark-themed.
 *    → Pendiente: definir posición y estilos en BioTemplatePremium.
 *
 * Ruta: /test
 */

import { BRAND_FAN_URL, BRAND_PERSON_NAME, BRAND_SITE_URL, BRAND_OG_IMAGE_URL } from "@/lib/brandConfig";
import { BaseTemplate } from "@/components/templates/BaseTemplate";
import { isCloudinaryImage, isSameCloudinaryImage } from "@/lib/cloudinaryUtils";
import { useCurrentTemplate } from "@/contexts/CurrentTemplateContext";
import { useSEO } from "@/hooks/useSEO";
import { usePortfolioSections } from "@/hooks/usePortfolioSections";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { useTemplateSelection } from "@/hooks/useTemplateSelection";
import { useEffect, useMemo } from "react";
import { Loader2 } from "lucide-react";
import { ArgentinaProvinceAdmin } from "@/components/dev/ArgentinaProvinceAdmin";

// ─── Botón BaseTemplate: AR, UY, ES, AU, algunos estados US ─────────────────
function BirthdayButton() {
  return (
    <a
      href={`${BRAND_FAN_URL}/r/cumple`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-3 py-3.5 px-5 bg-black/[0.08] backdrop-blur-[16px] border border-black/[0.15] rounded-[14px] hover:bg-black/[0.12] hover:border-black/[0.25] transition-all duration-200 max-w-[360px] mx-auto"
    >
      <span className="text-2xl flex-shrink-0" role="img" aria-label="Pastel de cumpleaños">
        🎂
      </span>
      <span className="text-[15px] font-semibold text-black leading-tight">
        Sugerencias para mi Cumpleaños
      </span>
    </a>
  );
}

// ─── Botón BioTemplatePremium: MX, BR, CO, CL, PE, Europa, etc. ─────────────
// NOTA: este botón se muestra en BaseTemplate (fondo blanco) solo para testear
// la lógica de detección por país. En producción irá en BioTemplatePremium
// (fondo negro) y necesitará estilo dark-themed (border-white/[0.15], text-white).
function WishlistButton() {
  return (
    <a
      href={`${BRAND_FAN_URL}/`}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-3 py-3.5 px-5 bg-black/[0.08] backdrop-blur-[16px] border border-black/[0.15] rounded-[14px] hover:bg-black/[0.12] hover:border-black/[0.25] transition-all duration-200 max-w-[360px] mx-auto"
    >
      <span className="text-2xl flex-shrink-0" role="img" aria-label="Regalo">
        🎁
      </span>
      <span className="text-[15px] font-semibold text-black leading-tight">
        Ingresa a mi wishlist de cumpleaños
      </span>
    </a>
  );
}

// ─── Página ───────────────────────────────────────────────────────────────────
export default function TestCTAButtons() {
  // ── Idéntico a Index.tsx ──────────────────────────────────────────────────
  const templateSelection = useTemplateSelection();
  const { templateName, country, showWishlist } = templateSelection;
  const { setTemplateName } = useCurrentTemplate();

  useEffect(() => {
    setTemplateName(templateName ?? null);
    return () => setTemplateName(null);
  }, [templateName, setTemplateName]);

  const region = country === "US" ? "US" : country === "ES" ? "SPAIN" : "LATAM";

  const { settings: siteSettings, isLoading: siteSettingsLoading } = useSiteSettings();
  const { allImageUrls: portfolioImageUrls, isLoading: portfolioLoading } = usePortfolioSections();

  const isBaseTemplate = !["ArgentinaBioTemplate", "BioLinkTemplate", "MaintenanceTemplate", "BioTemplatePremium"].includes(templateName ?? "");

  useSEO({
    title: region === "US"
      ? `${BRAND_PERSON_NAME} | Join My Community - Premium Content & Exclusive Access`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME} | Únete a Mi Comunidad - Contenido Premium y Acceso Exclusivo`
      : isBaseTemplate
      ? siteSettings.title
      : `${BRAND_PERSON_NAME} | Modelo y Content Creator Argentina`,
    description: region === "US"
      ? `Join ${BRAND_PERSON_NAME}'s exclusive community! Get access to premium Instagram content, join our Telegram group, and connect directly. Your gateway to exclusive lifestyle content and personal interaction.`
      : region === "SPAIN"
      ? `¡Únete a la comunidad exclusiva de ${BRAND_PERSON_NAME}! Accede a contenido premium de Instagram, únete a nuestro grupo de Telegram y conecta directamente. Tu puerta de entrada al contenido exclusivo de lifestyle e interacción personal.`
      : isBaseTemplate
      ? siteSettings.description
      : "Modelo y creadora de contenido argentina, con identidad estética definida y un universo visual muy propio. Referente en moda y estilo de vida para audiencias jóvenes. Disponible para campañas y colaboraciones con marcas.",
    keywords: region === "US"
      ? `${BRAND_PERSON_NAME}, premium content, exclusive access, Instagram content, Telegram group, bio link, content creator, lifestyle, community, exclusive content`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME}, contenido premium, acceso exclusivo, contenido Instagram, grupo Telegram, bio link, creadora contenido, lifestyle, comunidad, contenido exclusivo, España`
      : `${BRAND_PERSON_NAME}, modelo argentina, content creator, moda, lifestyle, campañas, colaboraciones, influencer, belleza`,
    ogImage: region === "US" || region === "SPAIN"
      ? BRAND_OG_IMAGE_URL
      : BRAND_OG_IMAGE_URL,
    ogTitle: region === "US"
      ? `${BRAND_PERSON_NAME} | Join My Community - Exclusive Content Access`
      : region === "SPAIN"
      ? `${BRAND_PERSON_NAME} | Únete a Mi Comunidad - Acceso a Contenido Exclusivo`
      : isBaseTemplate
      ? siteSettings.title
      : `${BRAND_PERSON_NAME} | Modelo y Content Creator Argentina`,
    ogDescription: region === "US"
      ? "Join my exclusive community! Premium Instagram access, Telegram group, and direct connection. Let's create amazing memories together! ✨"
      : region === "SPAIN"
      ? "¡Únete a mi comunidad exclusiva! Acceso premium a Instagram, grupo de Telegram y conexión directa. ¡Vamos a crear recuerdos increíbles juntos! ✨"
      : isBaseTemplate
      ? siteSettings.description
      : "Modelo y creadora de contenido argentina, con identidad estética definida y un universo visual muy propio. Referente en moda y estilo de vida. Disponible para campañas y colaboraciones con marcas.",
    canonical: `${BRAND_SITE_URL}/test`,
    author: BRAND_PERSON_NAME,
    publishedTime: "2026-01-01T00:00:00.000Z",
    modifiedTime: new Date().toISOString(),
    type: region === "US" ? "profile" : region === "SPAIN" ? "profile" : "website",
    locale: region === "US" ? "en_US" : region === "SPAIN" ? "es_ES" : "es_AR",
    alternateLocale: region === "US" ? "es_AR" : region === "SPAIN" ? "en_US" : "en_US",
    robots: "noindex, nofollow",
  });

  const siteGalleryUrls = (siteSettings.galleryImageUrls ?? [])
    .filter((u): u is string => typeof u === "string" && u.trim().length > 0 && isCloudinaryImage(u))
    .map((u) => u.trim());

  const logoUrl = (siteSettings.logoUrl as string)?.trim() || "";

  const galleryWithoutLogo = logoUrl
    ? siteGalleryUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : siteGalleryUrls;

  const portfolioUrls = portfolioImageUrls
    .filter((u) => u?.trim() && isCloudinaryImage(u))
    .map((u) => u.trim());

  const portfolioWithoutLogo = logoUrl
    ? portfolioUrls.filter((u) => !isSameCloudinaryImage(logoUrl, u))
    : portfolioUrls;

  const baseTemplateGalleryItems = useMemo(() => {
    if (portfolioWithoutLogo.length === 0) return [];
    const shuffled = [...portfolioWithoutLogo].sort(() => Math.random() - 0.5);
    return shuffled.map((src, i) => ({
      id: i + 1,
      type: "photo" as const,
      src: src.trim(),
      title: "",
    }));
  }, [portfolioWithoutLogo]);

  // ── Loading guard (igual que Index.tsx) ──────────────────────────────────
  const baseTemplateLoading = siteSettingsLoading || portfolioLoading;
  if (baseTemplateLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white">
        <Loader2 className="w-10 h-10 text-black/40 animate-spin mb-4" aria-hidden />
        <p className="text-sm text-black/50">Cargando...</p>
      </div>
    );
  }

  const bannerImageUrl =
    typeof siteSettings.bannerImageUrl === "string" && siteSettings.bannerImageUrl.trim()
      ? siteSettings.bannerImageUrl
      : portfolioWithoutLogo.length > 0
        ? portfolioWithoutLogo[0]
        : undefined;

  // Botón según la detección geo del Cloudflare Worker:
  // AR + provincia/ciudad en AR_WISHLIST_PROVINCES/CITIES (Worker) → Wishlist
  // AR resto de provincias/ciudades                                → Cumpleaños
  // Otros países BaseTemplate (UY, ES, AU)                        → Cumpleaños
  const heroCta = showWishlist ? <WishlistButton /> : <BirthdayButton />;

  // ── Render igual que Index.tsx + heroCta (único agregado) ─────────────────
  return (
    <>
      <BaseTemplate
        mediaItems={baseTemplateGalleryItems}
        skipMuxApiFilter={true}
        headerUrl={siteSettings.headerUrl}
        logoUrl={siteSettings.logoUrl}
        heroTitle={siteSettings.title}
        heroDescription={siteSettings.description}
        bannerImageUrl={bannerImageUrl}
        bannerDeferred={false}
        galleryDeferred={false}
        heroCta={heroCta}
      />
      <ArgentinaProvinceAdmin />
    </>
  );
}
